import { useRef, useState } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import useClickOutside from "../../../../hooks/useClickOutside";
import SvgIcon from "../../../../parts/SvgIcon";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";

export const SettingsCommunityChatOptions = ({
  displayState,
  webAppTheme,
  directMessagesEnabled,
  onSort,
  onDisplay,
}) => {
  const dropdownRef = useRef(null);
  const [selectedSortOption, setSelectedSortOption] = useState("sort-recent");
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const options = [
    {
      label: "Display",
      value: "display",
      type: "hover",
      sublist: [
        {
          sublabel: "Show All",
          subvalue: "show-all",
        },
        {
          sublabel: "Show Channels Only",
          subvalue: "show-channels",
        },
        {
          sublabel: "Show Messages Only",
          subvalue: "show-messages",
        },
      ],
    },
    {
      label: "Sort",
      value: "sort",
      type: "hover",
      sublist: [
        {
          sublabel: "Ascending",
          subvalue: "sort-ascending",
          icon: "ascending",
        },
        {
          sublabel: "Descending",
          subvalue: "sort-descending",
          icon: "descending",
        },
        {
          sublabel: "By Most Recent",
          subvalue: "sort-recent",
          icon: "coaching-request",
        },
      ],
    },
    { label: "Add New Channel", value: "new-community-channel", type: "modal" },
    { label: "New Message", value: "new-community-message", type: "modal" },
    { label: "Direct Message", value: "direct-message", type: "toggle" },
  ];

  /**
   * Close the dropdown clicking outside
   */
  useClickOutside(
    dropdownRef,
    () => {
      setDropdownOpened(false);
    },
    dropdownOpened
  );
  /**
   * Open the dropdown
   * @param {*} e
   */
  const handleOpen = (e) => {
    e.preventDefault();
    setDropdownOpened(!dropdownOpened);
  };

  /**
   * Select an option of the dropdown and open a modal
   * @param {*} e
   * @param {*} value
   */
  const handleModalClick = (e, value) => {
    e.preventDefault();
    setDropdownOpened(!dropdownOpened);
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        "modal[type]": value,
      },
    });
  };

  /**
   * Select a sub option to sort o display the content
   * @param {*} type
   * @param {*} value
   */
  const handleSubListClick = (type, value) => {
    if (type === "sort") {
      handleSortClick(value);
    } else if (type === "display") {
      handleDisplayClick(value);
    }
  };

  /**
   * Select a sort option
   * @param {*} value
   */
  const handleSortClick = (value) => {
    setDropdownOpened(false);
    setSelectedSortOption(value);
    onSort(value);
  };

  /**
   * Select a display option
   * @param {*} value
   */
  const handleDisplayClick = (value) => {
    setDropdownOpened(false);
    onDisplay(value);
  };

  return (
    <details id="dropdown-options" role="list" open ref={dropdownRef}>
      <summary onClick={handleOpen}>
        <img
          src="/img/new-icons/settings.svg"
          alt="settings"
          className={webAppTheme === "dark" ? "filter-white" : "filter-black"}
        />
      </summary>
      <div className={dropdownOpened ? "opened" : ""}>
        <ul role="listbox">
          {options.map(({ label, value, type, sublist }, index) => {
            return (
              <li
                key={value}
                onClick={
                  type === "modal"
                    ? (e) => handleModalClick(e, value)
                    : undefined
                }
              >
                <details open>
                  <summary>
                    <span>{label}</span>
                    {(type === "hover" || type === "modal") && (
                      <SvgIcon
                        src={
                          type === "hover"
                            ? "/img/new-icons/arrow-right.svg"
                            : "/img/new-icons/add.svg"
                        }
                      />
                    )}
                    {type === "toggle" && (
                      <DirectMessageToggle
                        directMessagesEnabled={directMessagesEnabled}
                        setDropdownOpened={setDropdownOpened}
                      />
                    )}
                  </summary>
                  {type === "hover" && !!sublist && (
                    <ul role="listbox">
                      {sublist.map(({ sublabel, subvalue, icon }) => {
                        return (
                          <li
                            key={subvalue}
                            onClick={() => {
                              handleSubListClick(value, subvalue);
                            }}
                          >
                            <div>
                              {label === "Sort" && (
                                <SvgIcon src={`/img/new-icons/${icon}.svg`} />
                              )}
                              <span>{sublabel}</span>
                            </div>
                            {(selectedSortOption === subvalue ||
                              displayState === subvalue) && (
                              <SvgIcon
                                src="/img/new-icons/tick-square.svg"
                                noFilterOnLight={true}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </details>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
};

SettingsCommunityChatOptions.propTypes = {
  displayState: PropTypes.string,
  webAppTheme: PropTypes.string.isRequired,
  directMessagesEnabled: PropTypes.bool.isRequired,
  onSort: PropTypes.func,
  onDisplay: PropTypes.func,
};

/**
 * Change the direct message option
 * @param {*} param0
 * @returns
 */
const DirectMessageToggle = ({ directMessagesEnabled, setDropdownOpened }) => {
  const [toggleState, setToggleState] = useState({
    enable: directMessagesEnabled,
    modal: false,
  });

  const { enable, modal } = toggleState;

  /**
   * Enable or disable the option
   */
  const handleToggle = () => {
    if (!enable) {
      handleUpdatePreference(true);
      setToggleState({
        enable: true,
        modal: false,
      });
    } else {
      setToggleState({
        enable: true,
        modal: true,
      });
    }
  };

  /**
   * When the user press the cancel option
   */
  const handleCancelModal = () => {
    setToggleState({
      enable: true,
      modal: false,
    });
    setDropdownOpened(false);
  };

  /**
   * When the user confirm to disable the option
   */
  const handleConfirmModal = () => {
    handleUpdatePreference(false);
    setToggleState({
      enable: false,
      modal: false,
    });
    setDropdownOpened(false);
  };

  /**
   * API request
   * @param {*} value
   */
  const handleUpdatePreference = (value) => {
    submit(`/actions/community-chat/direct-message-toggle`, {
      method: "POST",
      body: {
        directMessagesEnabled: value,
      },
    });
  };

  return (
    <>
      <input
        type="checkbox"
        id={`switch`}
        name={`switch`}
        role="switch"
        onChange={handleToggle}
        checked={enable}
      />
      <ConfirmationModal
        isOpen={modal}
        title={`Turn off direct message`}
        contentText={`Are you sure you want to turn off direct messages for your community? Turning off direct messages will make your community channel-based only chat.`}
        cancelButtonText="Cancel"
        cancelButtonAction={handleCancelModal}
        confirmButtonText="Turn Off"
        confirmButtonAction={handleConfirmModal}
      />
    </>
  );
};
